import api from './api';

/**
 * Create a new grade snapshot
 * @param {string} userId - Canvas user ID
 * @param {Object} snapshotData - The snapshot data to store
 * @returns {Promise<Object>} - The saved snapshot with server-generated ID
 */
export const createSnapshot = async (userId, snapshotData) => {
  if (!userId) {
    throw new Error('Canvas user ID is required to create a snapshot');
  }
  
  try {
    const headers = {
      'X-Canvas-User-Id': userId
    };
    const response = await api.post('/snapshots', snapshotData, { headers });
    return response;
  } catch (error) {
    console.error('Failed to create snapshot:', error);
    throw new Error('Failed to save your grade snapshot. Please try again.');
  }
};

/**
 * Get all snapshots for the current user
 * @param {string} userId - Canvas user ID
 * @returns {Promise<Array>} - Array of snapshots
 */
export const getSnapshots = async (userId) => {
  if (!userId) {
    throw new Error('Canvas user ID is required to retrieve snapshots');
  }
  
  try {
    // Set headers for authentication and Canvas credentials
    const headers = {
      'X-Canvas-User-Id': userId
    };
    
    const response = await api.get(`/snapshots`, { headers });
    return response;
  } catch (error) {
    console.error('Failed to retrieve snapshots:', error);
    throw new Error('Failed to load your grade snapshots. Please try again.');
  }
};

/**
 * Get a single snapshot's full body by ID.
 *
 * Task 6.0ad: GetSnapshots now returns per-snapshot summaries only
 * (courseSummaries, no assignment data), so the four consumers that need
 * the full body - offline entry, offline child switch, /compare - fetch
 * it here by id. Unlike the other functions in this file, errors are
 * *not* caught and rewrapped: callers need the original axios error so
 * they can read `error.response?.status` (404 = not found/not yours,
 * byte-identical) and distinguish it from a network failure.
 * @param {string} userId - Canvas user ID
 * @param {string} snapshotId - The ID of the snapshot to retrieve
 * @returns {Promise<Object>} - The snapshot's full body ({id, date, createdAt, courses})
 */
export const getSnapshot = async (userId, snapshotId) => {
  if (!userId) {
    throw new Error('Canvas user ID is required to retrieve a snapshot');
  }

  const headers = {
    'X-Canvas-User-Id': userId
  };

  return api.get(`/snapshots/${encodeURIComponent(snapshotId)}`, { headers });
};

/**
 * Delete a snapshot by ID
 * @param {string} userId - Canvas user ID
 * @param {string} snapshotId - The ID of the snapshot to delete
 * @returns {Promise<Object>} - Success response
 */
export const deleteSnapshot = async (userId, snapshotId) => {
  if (!userId) {
    throw new Error('Canvas user ID is required to delete a snapshot');
  }

  try {
    const headers = {
      'X-Canvas-User-Id': userId
    };

    const response = await api.delete(`/snapshots/${encodeURIComponent(snapshotId)}`, { headers });
    return response;
  } catch (error) {
    console.error(`Failed to delete snapshot ${snapshotId}:`, error);
    throw new Error('Failed to delete the snapshot. Please try again.');
  }
};

const snapshotService = {
  createSnapshot,
  getSnapshots,
  getSnapshot,
  deleteSnapshot
};

export default snapshotService;